import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Button, Tooltip } from '@mui/material';
import { useEnqueueSnackbar } from 'hooks';
import type { PackEntry } from 'types/shared';
import {
  buildPackEntry,
  getTestCreativesIdToHypothesis,
  groupIdsByHypothesis,
  packTagForHypothesis,
} from '../utils/hypothesisPackUtils';

const AddTestingPackButton = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();
  const selectedCreatives = watch('selectedCreatives') as string[];
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const testCreativesTab = watch('testCreativesTab') as string | undefined;

  // All IDs already committed to any pack.
  const packedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const pack of packsWithCreatives) {
      for (const id of pack.creatives) if (id !== undefined) ids.add(id);
    }
    return ids;
  }, [packsWithCreatives]);

  // At least one selected creative must be unpacked (from "Creatives not in pack").
  const hasUnpackedSelection = selectedCreatives.some((id) => !packedIds.has(id));

  const isAddPackAvailable =
    testCreativesTab === 'test_creatives' &&
    selectedCreatives.length > 0 &&
    hasUnpackedSelection;

  const handleAddPack = useCallback(() => {
    const idToHypothesis = getTestCreativesIdToHypothesis();
    const groupedByHypothesis = groupIdsByHypothesis(
      selectedCreatives.filter((id) => !packedIds.has(id)),
      idToHypothesis,
    );

    const newPacks: PackEntry[] = [...groupedByHypothesis.entries()].map(
      ([hypothesisLabel, creatives]) =>
        buildPackEntry(packTagForHypothesis(hypothesisLabel), creatives),
    );

    if (!newPacks.length) return;

    const updatedPacks = [...packsWithCreatives, ...newPacks];
    setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
    setValue('activePacksWithCreatives', packsWithCreatives.length);
    setValue('selectedCreatives', [], { shouldValidate: true });

    onShowAlert(
      newPacks.length === 1 ? 'Pack added' : `${newPacks.length} packs added`,
      { delayAlert: 3500 },
    );

    void trigger('packsWithCreatives');
  }, [
    selectedCreatives,
    packedIds,
    packsWithCreatives,
    setValue,
    trigger,
    onShowAlert,
  ]);

  return (
    <Tooltip title={'Select creatives to add a new pack'} placement={'top'}>
      <Box>
        <Button
          disabled={!isAddPackAvailable}
          variant="outlined"
          color="primary"
          onClick={handleAddPack}
        >
          Add pack
        </Button>
      </Box>
    </Tooltip>
  );
};

export default AddTestingPackButton;
